import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { DirectionsCar as CarIcon, Person as PersonIcon, Place as PlaceIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useCalendarColors from '../hooks/useCalendarColors';
import useCalendarState from '../hooks/useCalendarState';

const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#ef4444', bg: '#fee2e2' },
  ordered: { color: '#d97706', bg: '#fef3c7' },
  arrived: { color: '#059669', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

function MobileEventCard({ event, style, onClick, isAllView, employees, narrow, serviceBays = [], showServiceBay = true }) {
  const { t } = useTranslation();
  const { highlightedMaintenanceId } = useCalendarState();
  const { resolveEvent } = useCalendarColors();

  // Highlighted after a calendar search or a jump from the work order page.
  const isHighlighted = highlightedMaintenanceId === event.maintenance_id;
  // The card resolves its own colours (state overrides + colour tag); the parent only
  // supplies geometry through `style`.
  const colors = resolveEvent({ ...event, state: event.state || event.status }, isHighlighted);
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    if (isHighlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);

  // Format time display (HH:mm with colon separator)
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Format short date (e.g., "7.1.")
  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}.${date.getMonth() + 1}.`;
  };

  const startDate = new Date(event.start_time);
  const endDate = new Date(event.end_time);
  const isMultiDay = event._isMultiDay || (startDate.toDateString() !== endDate.toDateString());

  // Build the time display string
  const getTimeDisplayString = () => {
    if (isMultiDay) {
      // Multi-day: show "7.1. 08:00 - 8.1. 10:00"
      return `${formatShortDate(event.start_time)} ${formatTime(event.start_time)} - ${formatShortDate(event.end_time)} ${formatTime(event.end_time)}`;
    } else {
      // Single-day: show "08:00 - 10:00"
      return `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`;
    }
  };

  const timeDisplayString = getTimeDisplayString();

  // Determine if we have enough space for full content
  const isCompact = style.height < 50;
  const isVeryCompact = style.height < 35;

  // Get employee names for "all" view display
  const getEmployeeNames = () => {
    if (!isAllView || !employees || !event.employee_ids?.length) return null;
    const names = event.employee_ids
      .map((id) => employees.find((e) => e.id === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : null;
  };

  const employeeNames = getEmployeeNames();

  // Service bay name (hidden where the column or header already names the bay)
  const serviceBayName = showServiceBay && event.service_bay_id
    ? serviceBays.find((b) => b.id === event.service_bay_id)?.name || ''
    : '';

  return (
    <Box
      ref={cardRef}
      data-testid={`calendar-event-${event.id}`}
      data-color-tag={event.color_tag_id != null ? String(event.color_tag_id) : undefined}
      data-color-tag-mode={colors.tagMode || undefined}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        position: 'absolute',
        // Use dynamic positioning from style for overlap handling
        left: style.left || 4,
        width: style.width || 'calc(100% - 8px)',
        top: style.top,
        height: style.height,
        bgcolor: colors.bg,
        borderLeft: '4px solid',
        borderLeftColor: colors.border,
        outline: isHighlighted
          ? '2px solid #ed8936'
          : colors.ringColor
          ? `2px solid ${colors.ringColor}`
          : 'none',
        outlineOffset: colors.ringColor && !isHighlighted ? '-2px' : 0,
        borderRadius: '4px',
        padding: narrow ? '2px 3px' : isVeryCompact ? '2px 6px' : '4px 8px',
        borderLeftWidth: narrow ? '3px' : '4px',
        cursor: 'pointer',
        overflow: 'hidden',
        zIndex: 2,
        transition: 'transform 0.1s, box-shadow 0.1s',
        '&:active': {
          transform: 'scale(0.98)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
      }}
    >
      {narrow ? (
        // Narrow column layout — stacked vertically, small font
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: colors.text,
              fontSize: '9px',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.vehicle_registration || event.title}
          </Typography>
          {style.height >= 30 && (
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.8,
                fontSize: '8px',
                lineHeight: 1.2,
              }}
            >
              {formatTime(event.start_time)}
            </Typography>
          )}
          {style.height >= 45 && event.customer_name && (
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.7,
                fontSize: '8px',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {event.customer_name}
            </Typography>
          )}
          {style.height >= 60 && serviceBayName && (
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.7,
                fontSize: '8px',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              📍 {serviceBayName}
            </Typography>
          )}
        </Box>
      ) : isVeryCompact ? (
        // Very compact - just registration number
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <CarIcon sx={{ fontSize: 12, color: colors.text }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: colors.text,
              fontSize: '10px',
              lineHeight: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.vehicle_registration || event.title}
          </Typography>
        </Box>
      ) : isCompact ? (
        // Compact - registration + time
        <Box sx={{ height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CarIcon sx={{ fontSize: 14, color: colors.text }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: colors.text,
                fontSize: '11px',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {event.vehicle_registration || event.title}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: colors.text,
              opacity: 0.85,
              fontSize: '10px',
              lineHeight: 1,
            }}
          >
            {timeDisplayString}
          </Typography>
        </Box>
      ) : (
        // Full content
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header with registration */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <CarIcon sx={{ fontSize: 16, color: colors.text }} />
            <Box sx={{ overflow: 'hidden', flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  fontSize: '13px',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {event.vehicle_registration || event.title}
              </Typography>
              {(event.vehicle_manufacturer || event.vehicle_model) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text,
                    opacity: 0.75,
                    fontSize: '10px',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {[event.vehicle_manufacturer, event.vehicle_model].filter(Boolean).join(' ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Customer name */}
          {event.customer_name && (
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.9,
                fontSize: '11px',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {event.customer_name}
            </Typography>
          )}

          {/* Employee names in "all" view */}
          {isAllView && employeeNames && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 11, color: colors.text, opacity: 0.8 }} />
              <Typography
                variant="caption"
                sx={{
                  color: colors.text,
                  opacity: 0.8,
                  fontSize: '10px',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {employeeNames}
              </Typography>
            </Box>
          )}

          {/* Time */}
          <Typography
            variant="caption"
            sx={{
              color: colors.text,
              opacity: 0.85,
              fontSize: '11px',
              lineHeight: 1.3,
              mt: 'auto',
            }}
          >
            {timeDisplayString}
          </Typography>

          {/* Parts order status badge */}
          {event.parts_order_status && PARTS_ORDER_CONFIG[event.parts_order_status] && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                mt: 0.25,
                padding: '1px 5px',
                borderRadius: '10px',
                fontSize: '9px',
                fontWeight: 500,
                backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].bg,
                color: PARTS_ORDER_CONFIG[event.parts_order_status].color,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                whiteSpace: 'nowrap',
              }}
            >
              <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].color }} />
              {t(`parts_order.${event.parts_order_status}_short`)}
            </Box>
          )}

          {/* Service bay (after the pinned time row and the parts badge so neither moves) */}
          {style.height >= 70 && serviceBayName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <PlaceIcon sx={{ fontSize: 11, color: colors.text, opacity: 0.8 }} />
              <Typography
                variant="caption"
                sx={{
                  color: colors.text,
                  opacity: 0.8,
                  fontSize: '10px',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {serviceBayName}
              </Typography>
            </Box>
          )}

          {/* Description preview if there's space */}
          {style.height >= 80 && event.description && (
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.8,
                fontSize: '10px',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                mt: 0.5,
              }}
            >
              {event.description}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default MobileEventCard;
